import { useState, useEffect, useCallback, useRef } from 'react';
import { AuthConfig, AuthState, UseAuthReturn, AuthResponse, JWTPayload } from '../types';
import { TokenManager } from '../utils/TokenManager';
import { resolveSuperAdminFlags, isSuperAdminUser } from '../utils/superAdmin';

/**
 * 初始化认证状态（检查本地缓存）
 */
const getInitialAuthState = (config: AuthConfig): AuthState => {
  // 先设置TokenManager的key，以便正确获取token
  TokenManager.setTokenKey(config.tokenKey, config.refreshTokenKey);

  const isEmbedded =
    typeof window !== 'undefined' && window.parent !== window;

  /**
   * iframe 内绝不信任本地缓存「已登录」直出：否则路由会在同源 POST /api/auth/verify 完成前挂载，
   * 仪表盘等会抢先发 GET /api/...，出现无 Authorization 或竞态 401。
   * Token 仍留在 localStorage，checkAuth / applyTokenFromParent 会照常读取并验票。
   */
  if (isEmbedded) {
    return {
      isAuthenticated: false,
      token: null,
      user: null,
      loading: true,
      error: null,
      permissions: [],
    };
  }

  // 直接检查localStorage，不依赖TokenManager（因为TokenManager可能还没初始化）
  // 检查多个可能的token key
  const token = localStorage.getItem(config.tokenKey) ||
    sessionStorage.getItem(config.tokenKey) ||
    localStorage.getItem('mslogin_token') ||
    sessionStorage.getItem('mslogin_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token');

  // 检查缓存的用户信息
  const cachedUser = localStorage.getItem('mslogin_user');

  // 如果有token和缓存的用户信息，使用缓存，避免显示loading
  if (token && cachedUser) {
    try {
      const userData = JSON.parse(cachedUser);
      const permissions = userData.permissions || [];

      // 确保用户数据格式正确
      if (userData && (userData.id || userData.account || userData.adminId)) {
        console.log('[useAuthWithConfig] 使用缓存初始化状态:', {
          hasToken: !!token,
          hasUser: !!userData,
          permissionsCount: permissions.length
        });

        return {
          isAuthenticated: true,
          token: token,
          user: userData,
          loading: false, // 使用缓存，不显示loading
          error: null,
          permissions: permissions,
        };
      } else {
        console.warn('[useAuthWithConfig] 缓存用户数据格式不正确:', userData);
      }
    } catch (error) {
      console.warn('[useAuthWithConfig] 缓存用户信息解析失败，使用默认状态:', error);
    }
  } else {
    // 即使没有缓存，如果有token，也先设置为已认证（但loading为true）
    // 这样可以避免显示"加载中"，让后台验证
    if (token) {
      console.log('[useAuthWithConfig] 有token但没有用户缓存，使用临时状态:', {
        hasToken: !!token,
        hasCachedUser: !!cachedUser
      });

      // 尝试从token中解析用户信息
      try {
        const payload = JSON.parse(atob(token.split('.')[1])) as JWTPayload;
        const permissions = payload.permissions || [];
        const base = {
          id: payload.adminId || payload.id,
          account: payload.account,
          admin_name: payload.account,
          roles: payload.roles || [],
          permissions,
          is_super_admin: payload.is_super_admin,
          isSuperAdmin: payload.isSuperAdmin,
        };
        const userData = {
          ...base,
          ...resolveSuperAdminFlags(base as Record<string, unknown>),
        };

        // 保存到缓存
        localStorage.setItem('mslogin_user', JSON.stringify(userData));

        return {
          isAuthenticated: true,
          token: token,
          user: userData,
          loading: false, // 使用token解析的数据，不显示loading
          error: null,
          permissions: userData.permissions,
        };
      } catch (parseError) {
        console.warn('[useAuthWithConfig] 无法从token解析用户信息:', parseError);
      }
    } else {
      console.log('[useAuthWithConfig] 没有缓存和token，使用默认状态:', {
        hasToken: !!token,
        hasCachedUser: !!cachedUser
      });
    }
  }

  // 没有缓存时，返回默认状态
  return {
    isAuthenticated: false,
    token: null,
    user: null,
    loading: true,
    error: null,
    permissions: [],
  };
};

/**
 * 配置化的认证Hook
 * 支持不同微服务的认证配置
 */
const isAuthDebug =
  typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

export const useAuthWithConfig = (config: AuthConfig): UseAuthReturn => {
  if (isAuthDebug) {
    console.log('[useAuthWithConfig] Hook 开始初始化，配置:', config);
  }

  // 在初始化时就检查缓存
  const [authState, setAuthState] = useState<AuthState>(() => getInitialAuthState(config));

  /** 用 ref 防抖复入，避免 isChecking 进入 useCallback 依赖导致 identity 变化 → message 监听 effect 反复重绑 */
  const isCheckingRef = useRef(false);

  if (isAuthDebug) {
    console.log('[useAuthWithConfig] 初始状态:', authState);
  }

  // 设置TokenManager的key
  useEffect(() => {
    TokenManager.setTokenKey(config.tokenKey, config.refreshTokenKey);
  }, [config.tokenKey, config.refreshTokenKey]);

  // 检查认证状态
  const checkAuth = useCallback(async (silent: boolean = false) => {
    // 防止重复调用
    if (isCheckingRef.current) {
      if (isAuthDebug) {
        console.log('[useAuthWithConfig] 认证检查正在进行中，跳过重复调用');
      }
      return;
    }

    if (isAuthDebug) {
      console.log('[useAuthWithConfig] checkAuth 函数被调用', silent ? '(静默模式)' : '');
    }
    isCheckingRef.current = true;
    // 静默模式下不设置loading，避免页面闪烁
    if (!silent) {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
    }

    try {
      const token = TokenManager.getCurrentToken();

      console.log('[useAuthWithConfig] Token检查结果:', {
        hasToken: !!token,
        tokenLength: token ? token.length : 0,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
        config: {
          serviceName: config.serviceName,
          apiBaseUrl: config.apiBaseUrl,
          authEndpoint: config.authEndpoint,
        }
      });

      if (!token) {
        console.log('[useAuthWithConfig] 没有找到token，认证失败');
        setAuthState({
          isAuthenticated: false,
          token: null,
          user: null,
          loading: false,
          error: null,
          permissions: [],
        });
        return;
      }

      // 验证token的有效性
      try {
        const trimmedVerify = config.authVerifyBaseUrl?.trim().replace(/\/$/, '');
        const authApiUrl =
          trimmedVerify ||
          (config.msLoginUrl || '').trim().replace(/\/$/, '') ||
          (config.apiBaseUrl || '').trim().replace(/\/$/, '');
        console.log('[useAuthWithConfig] 使用API配置:', {
          apiBaseUrl: config.apiBaseUrl,
          msLoginUrl: config.msLoginUrl,
          authEndpoint: config.authEndpoint,
          authApiUrl: authApiUrl
        });
        console.log('[useAuthWithConfig] 发送认证请求到:', `${authApiUrl}${config.authEndpoint}`);

        const response = await fetch(`${authApiUrl}${config.authEndpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        console.log('[useAuthWithConfig] 认证响应状态:', response.status);
        const result: AuthResponse = await response.json();
        console.log('[useAuthWithConfig] 认证响应结果:', result);

        if (result.success && result.data?.valid) {
          console.log('[useAuthWithConfig] 认证成功，设置认证状态');
          // 支持两种响应格式：
          // 1. result.data.admin (旧格式)
          // 2. result.data.payload (新格式，从 msLogin 服务返回)
          const adminData: any = result.data.admin || (result.data as any).payload;
          const permissions = adminData?.permissions || [];

          if (!adminData) {
            console.warn('[useAuthWithConfig] 认证响应中没有用户数据');
            TokenManager.clearAll();
            setAuthState({
              isAuthenticated: false,
              token: null,
              user: null,
              loading: false,
              error: '认证响应格式错误',
              permissions: [],
            });
            return;
          }

          const userData = {
            ...adminData,
            ...resolveSuperAdminFlags(adminData as Record<string, unknown>),
          };

          setAuthState({
            isAuthenticated: true,
            token,
            user: userData,
            loading: false,
            error: null,
            permissions,
          });

          // 立即保存用户信息到 localStorage，供其他实例使用
          try {
            localStorage.setItem('mslogin_user', JSON.stringify(userData));
          } catch (error) {
            console.warn('[useAuthWithConfig] 保存用户信息到localStorage失败:', error);
          }

          console.log('[useAuthWithConfig] 认证状态已设置:', {
            isAuthenticated: true,
            hasToken: !!token,
            hasUser: !!adminData,
            permissionsCount: permissions.length
          });
        } else {
          // Token 无效或服务端明确拒绝（含 HTTP 401）
          // 静默验票 silent 时若仍「不更新状态」，会长期保持缓存态为已登录 → 业务 API 仍 401 → 与 handleCrmAuthExpired 形成死循环
          // 仅当服务端 5xx 且为静默模式时暂不推翻本地态，避免短暂故障时误登出
          const status = response.status;
          if (silent && status >= 500) {
            return;
          }
          TokenManager.clearAll();
          try {
            localStorage.removeItem('mslogin_user');
          } catch {
            /* ignore */
          }
          setAuthState({
            isAuthenticated: false,
            token: null,
            user: null,
            loading: false,
            error:
              (result && (result as { message?: string }).message) ||
              (status === 401 || status === 403
                ? 'Token无效或已过期（请确认 CRM 后端 MSLOGIN_JWT_SECRET 与 msLogin JWT_SECRET 一致）'
                : `验票失败 (${status})`),
            permissions: [],
          });
        }
      } catch (verifyError) {
        // 如果验证失败，可能是网络问题或服务不可用
        // 暂时假设token有效，避免频繁的网络请求
        console.warn('Token验证失败，使用本地token:', verifyError);

        // 尝试从token中解析用户信息
        try {
          const payload = JSON.parse(atob(token.split('.')[1])) as JWTPayload;

          // 安全检查：确保 payload 和必要字段存在
          if (!payload || !payload.adminId || !payload.account) {
            throw new Error('Token payload 格式无效');
          }

          const roles = payload.roles || [];
          const permissions = payload.permissions || [];
          const rawJwt = {
            ...payload,
            permissions,
            roles,
          } as Record<string, unknown>;
          const userData = {
            id: payload.adminId,
            account: payload.account,
            admin_name: payload.account,
            roles: roles,
            permissions: permissions,
            ...resolveSuperAdminFlags(rawJwt),
          };

          setAuthState({
            isAuthenticated: true,
            token,
            user: userData,
            loading: false,
            error: null,
            permissions: permissions,
          });

          // 立即保存用户信息到 localStorage，供其他实例使用
          try {
            localStorage.setItem('mslogin_user', JSON.stringify(userData));
          } catch (error) {
            console.warn('[useAuthWithConfig] 保存用户信息到localStorage失败:', error);
          }
        } catch (parseError) {
          console.error('[useAuthWithConfig] Token解析失败:', parseError);
          setAuthState({
            isAuthenticated: false,
            token: null,
            user: null,
            loading: false,
            error: parseError instanceof Error ? parseError.message : 'Token格式无效',
            permissions: [],
          });
        }
      }
    } catch (error) {
      // 静默模式下，验证失败时不更新状态，保持当前状态
      if (!silent) {
        setAuthState({
          isAuthenticated: false,
          token: null,
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : '认证检查失败',
          permissions: [],
        });
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [
    config.apiBaseUrl,
    config.authVerifyBaseUrl,
    config.authEndpoint,
    config.serviceName,
    config.tokenKey,
    config.refreshTokenKey,
    config.msLoginUrl,
  ]);

  /**
   * msLogin ContainerPage iframe 场景：子域（如 crm.homaxs.com）与父页（www.homaxs.com）不同源，
   * 无法共享 localStorage。父页通过 postMessage 下发 TOKEN_PROVIDE，此处写入 TokenManager 并验票。
   * 与 msDevice/msSpace 等应用内手写逻辑对齐，集中到 shared-auth 供 mscrm 等使用。
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const applyTokenFromParent = (token: string | undefined) => {
      if (!token || typeof token !== 'string') return;
      TokenManager.setTokenKey(config.tokenKey, config.refreshTokenKey);
      // 与「本地已持有相同 token」去重；401 清空 crm_token 后父页再发同一 JWT 时必须重新写入，不能按 ref 去重跳过
      const existing = TokenManager.getCurrentToken();
      if (existing === token) {
        return;
      }
      TokenManager.setToken(token);
      try {
        localStorage.setItem('mslogin_token', token);
      } catch {
        // ignore quota / private mode
      }
      // 静默验票：避免与父页多条 TOKEN_* 叠加导致 loading 反复为 true（页面一直转圈）
      void checkAuth(true);
    };

    const handleMessage = (event: MessageEvent) => {
      const t = event.data?.token;
      const type = event.data?.type;
      if (!t || typeof t !== 'string') return;

      if (type === 'TOKEN_PROVIDE') {
        const svc = event.data?.service;
        if (svc && config.serviceName && svc !== config.serviceName) {
          return;
        }
        applyTokenFromParent(t);
        return;
      }
      if (type === 'TOKEN_UPDATE' || type === 'TOKEN_UPDATED') {
        applyTokenFromParent(t);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [config.tokenKey, config.refreshTokenKey, config.serviceName, checkAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.parent === window) return;
    const send = () => {
      window.parent.postMessage({ type: 'TOKEN_UPDATE_REQUEST' }, '*');
    };
    send();
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      send();
      if (n >= 8) window.clearInterval(id);
    }, 2000);
    return () => window.clearInterval(id);
  }, []);

  // 登录
  const login = useCallback(async (token: string) => {
    try {
      TokenManager.setToken(token);
      await checkAuth();
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : '登录失败',
      }));
    }
  }, [checkAuth]);

  // 登出
  const logout = useCallback(() => {
    TokenManager.clearAll();
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null,
      loading: false,
      error: null,
      permissions: [],
    });
  }, []);

  // 权限检查函数
  const hasPermission = useCallback((permission: string): boolean => {
    if (!authState.isAuthenticated || !authState.user) return false;

    // 超级管理员拥有所有权限
    if (isSuperAdminUser(authState.user)) return true;

    // 检查具体权限
    const userPermissions = authState.permissions || [];

    // 直接匹配
    if (userPermissions.includes(permission)) return true;

    // 通配符匹配：检查是否有通配符权限
    for (const userPerm of userPermissions) {
      if (userPerm.endsWith(':*')) {
        // 提取通配符前缀
        const wildcardPrefix = userPerm.slice(0, -2); // 移除 ':*'
        if (permission.startsWith(wildcardPrefix + ':')) {
          return true;
        }
      }
    }

    return false;
  }, [authState.isAuthenticated, authState.user, authState.permissions]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!authState.isAuthenticated || !authState.user) {
      console.log('[useAuthWithConfig] hasAnyPermission: 未认证或用户不存在', {
        isAuthenticated: authState.isAuthenticated,
        hasUser: !!authState.user,
      });
      return false;
    }

    // 超级管理员拥有所有权限
    if (isSuperAdminUser(authState.user)) {
      console.log('[useAuthWithConfig] hasAnyPermission: 超级管理员，拥有所有权限');
      return true;
    }

    // 检查是否有任一权限
    const userPermissions = authState.permissions || [];
    console.log('[useAuthWithConfig] hasAnyPermission: 检查权限', {
      requiredPermissions: permissions,
      userPermissions: userPermissions,
      userRoles: authState.user.roles || [],
    });

    const result = permissions.some(permission => {
      // 直接匹配
      if (userPermissions.includes(permission)) {
        console.log(`[useAuthWithConfig] hasAnyPermission: 直接匹配权限 '${permission}'`);
        return true;
      }

      // 通配符匹配
      for (const userPerm of userPermissions) {
        if (userPerm.endsWith(':*')) {
          const wildcardPrefix = userPerm.slice(0, -2);
          if (permission.startsWith(wildcardPrefix + ':')) {
            console.log(`[useAuthWithConfig] hasAnyPermission: 通配符匹配 '${userPerm}' -> '${permission}'`);
            return true;
          }
        }
      }
      return false;
    });

    console.log('[useAuthWithConfig] hasAnyPermission: 最终结果', result);
    return result;
  }, [authState.isAuthenticated, authState.user, authState.permissions]);

  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    if (!authState.isAuthenticated || !authState.user) return false;

    // 超级管理员拥有所有权限
    if (isSuperAdminUser(authState.user)) return true;

    // 检查是否有所有权限
    const userPermissions = authState.permissions || [];
    return permissions.every(permission => {
      // 直接匹配
      if (userPermissions.includes(permission)) return true;

      // 通配符匹配
      for (const userPerm of userPermissions) {
        if (userPerm.endsWith(':*')) {
          const wildcardPrefix = userPerm.slice(0, -2);
          if (permission.startsWith(wildcardPrefix + ':')) {
            return true;
          }
        }
      }
      return false;
    });
  }, [authState.isAuthenticated, authState.user, authState.permissions]);

  useEffect(() => {
    // 检查初始状态是否使用了缓存
    const token = TokenManager.getCurrentToken();
    const cachedUser = localStorage.getItem('mslogin_user');
    const hasCache = token && cachedUser;

    if (hasCache && !authState.loading && authState.isAuthenticated) {
      // 有缓存且已设置，延迟1秒后静默验证，避免影响页面切换
      const timer = setTimeout(() => {
        checkAuth(true).catch(() => {
          // 验证失败时保持缓存状态，不强制跳转
        });
      }, 1000);

      return () => clearTimeout(timer);
    } else if (!hasCache) {
      const embedded =
        typeof window !== 'undefined' && window.parent !== window;
      if (embedded) {
        // 等父窗口 TOKEN_UPDATE_REQUEST → TOKEN_PROVIDE（IframeRenderer 约 1s 后下发）
        const timer = setTimeout(() => {
          // 静默：避免与父页已下发的 token 叠加时再顶一次 loading
          checkAuth(true);
        }, 1200);
        return () => clearTimeout(timer);
      }
      checkAuth();
    } else {
      /**
       * 有 token+mslogin_user，但未命中「已认证且非 loading」首分支时（常见：iframe 首屏强制未登录；
       * 或独立窗口缓存异常），必须主动验票，否则会一直 loading / 卡在「正在验证登录…」。
       */
      const embedded =
        typeof window !== 'undefined' && window.parent !== window;
      if (embedded) {
        void checkAuth(true);
      } else {
        checkAuth();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时执行一次

  return {
    ...authState,
    checkAuth,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
